import React, { useCallback, useEffect, useState, useRef } from 'react';
function useReveal(delay = 0) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setTimeout(() => el.classList.add('in-view'), delay);
        observer.disconnect();
      }
    }, {
      threshold: 0.1
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, [delay]);
  return ref;
}
// ── Sticker images ──
const STICKERS = [{
  id: 0,
  src: 'https://cdn.magicpatterns.com/uploads/aSSN5r6FKeKAbPRYNBan8J/image_274.png',
  size: 80,
  rotation: -6
}, {
  id: 1,
  src: 'https://cdn.magicpatterns.com/uploads/rrfSzBNokG14ovpUK9u4hG/image_275.png',
  size: 95,
  rotation: 8
}, {
  id: 2,
  src: 'https://cdn.magicpatterns.com/uploads/3GSvRgSePdEvGatoAnT4mE/image_276.png',
  size: 100,
  rotation: -4
}, {
  id: 3,
  src: 'https://cdn.magicpatterns.com/uploads/7HLGZG9mz7AbGsG1SxoHct/image_277.png',
  size: 85,
  rotation: 5
}, {
  id: 4,
  src: 'https://cdn.magicpatterns.com/uploads/f7zBiriGL9mLxi2oR5aW5K/image_281.png',
  size: 92,
  rotation: -9
}, {
  id: 5,
  src: 'https://cdn.magicpatterns.com/uploads/u4mhrPp5twg46HbcEJboVW/image_282.png',
  size: 105,
  rotation: -5
}, {
  id: 6,
  src: 'https://cdn.magicpatterns.com/uploads/x2NieDLdgGtbev48WRC1kX/image_283.png',
  size: 82,
  rotation: 4
}, {
  id: 7,
  src: 'https://cdn.magicpatterns.com/uploads/cfEfoNEWsSeY92XvAYBcSf/image_280.png',
  size: 88,
  rotation: 6
}];
interface StickerPos {
  x: number;
  y: number;
}
function getDefaultStickerPositions(sectionW: number, sectionH: number): StickerPos[] {
  const isMobile = sectionW < 768;
  if (isMobile) {
    // Mobile: push stickers much further off-screen so they don't overlap text
    return [
    // LEFT edge stickers
    {
      x: -65,
      y: Math.round(sectionH * 0.03)
    },
    // RIGHT edge
    {
      x: sectionW - 20,
      y: Math.round(sectionH * 0.1)
    },
    // LEFT edge
    {
      x: -70,
      y: Math.round(sectionH * 0.22)
    },
    // RIGHT edge
    {
      x: sectionW - 22,
      y: Math.round(sectionH * 0.34)
    },
    // LEFT edge
    {
      x: -65,
      y: Math.round(sectionH * 0.46)
    },
    // RIGHT edge
    {
      x: sectionW - 18,
      y: Math.round(sectionH * 0.58)
    },
    // LEFT edge
    {
      x: -72,
      y: Math.round(sectionH * 0.72)
    },
    // RIGHT edge
    {
      x: sectionW - 20,
      y: Math.round(sectionH * 0.85)
    }];
  }
  return [{
    x: 14,
    y: 22
  }, {
    x: sectionW - 108,
    y: 14
  }, {
    x: 10,
    y: sectionH * 0.35
  }, {
    x: sectionW - 118,
    y: sectionH * 0.48
  }, {
    x: sectionW * 0.48,
    y: sectionH - 108
  }, {
    x: sectionW - 112,
    y: sectionH * 0.22
  }, {
    x: sectionW * 0.52,
    y: 18
  }, {
    x: sectionW - 115,
    y: sectionH - 112
  }];
}
export function About() {
  const headerRef = useReveal();
  const leftRef = useReveal(100);
  const rightRef = useReveal(200);
  const sectionRef = useRef<HTMLElement>(null);
  // ── Custom cursor ──────────────────────────────────────────────────────
  const [cursor, setCursor] = useState({
    x: 0,
    y: 0,
    visible: false
  });
  useEffect(() => {
    const section = sectionRef.current;
    if (!section) return;
    const onMove = (e: MouseEvent) => setCursor({
      x: e.clientX,
      y: e.clientY,
      visible: true
    });
    const onEnter = () => setCursor(c => ({
      ...c,
      visible: true
    }));
    const onLeave = () => setCursor(c => ({
      ...c,
      visible: false
    }));
    section.addEventListener('mousemove', onMove);
    section.addEventListener('mouseenter', onEnter);
    section.addEventListener('mouseleave', onLeave);
    return () => {
      section.removeEventListener('mousemove', onMove);
      section.removeEventListener('mouseenter', onEnter);
      section.removeEventListener('mouseleave', onLeave);
    };
  }, []);
  // ── Sticker drag state ─────────────────────────────────────────────────
  const [stickerPositions, setStickerPositions] = useState<StickerPos[]>([]);
  const [topZ, setTopZ] = useState(10);
  const [stickerZ, setStickerZ] = useState(STICKERS.map((_, i) => i + 1));
  const dragRef = useRef<{
    id: number;
    startMouseX: number;
    startMouseY: number;
    startCardX: number;
    startCardY: number;
  } | null>(null);
  const posRef = useRef<StickerPos[]>([]);
  posRef.current = stickerPositions;
  // Init sticker positions
  useEffect(() => {
    const section = sectionRef.current;
    if (!section) return;
    const recalc = () => {
      const w = section.offsetWidth;
      const h = section.offsetHeight;
      setStickerPositions(getDefaultStickerPositions(w, h));
    };
    // Initial calculation
    recalc();
    // Recalculate once after a short delay to catch layout shifts (images loading, etc.)
    const timer = setTimeout(recalc, 500);
    return () => clearTimeout(timer);
  }, []);
  // ── Mouse drag handlers ────────────────────────────────────────────────
  const handleStickerMouseDown = useCallback((e: React.MouseEvent, id: number) => {
    e.preventDefault();
    e.stopPropagation();
    const newZ = topZ + 1;
    setTopZ(newZ);
    setStickerZ(prev => {
      const next = [...prev];
      next[id] = newZ;
      return next;
    });
    dragRef.current = {
      id,
      startMouseX: e.clientX,
      startMouseY: e.clientY,
      startCardX: posRef.current[id].x,
      startCardY: posRef.current[id].y
    };
  }, [topZ]);
  // ── Touch drag handlers ────────────────────────────────────────────────
  const handleStickerTouchStart = useCallback((e: React.TouchEvent, id: number) => {
    e.preventDefault();
    e.stopPropagation();
    const touch = e.touches[0];
    const newZ = topZ + 1;
    setTopZ(newZ);
    setStickerZ(prev => {
      const next = [...prev];
      next[id] = newZ;
      return next;
    });
    dragRef.current = {
      id,
      startMouseX: touch.clientX,
      startMouseY: touch.clientY,
      startCardX: posRef.current[id].x,
      startCardY: posRef.current[id].y
    };
  }, [topZ]);
  // ── Global move / up / touchmove / touchend ────────────────────────────
  useEffect(() => {
    const onMove = (e: MouseEvent) => {
      const section = sectionRef.current;
      if (!section || !dragRef.current) return;
      const {
        id,
        startMouseX,
        startMouseY,
        startCardX,
        startCardY
      } = dragRef.current;
      const newX = startCardX + (e.clientX - startMouseX);
      const newY = startCardY + (e.clientY - startMouseY);
      setStickerPositions(prev => {
        const next = [...prev];
        next[id] = {
          x: newX,
          y: newY
        };
        return next;
      });
    };
    const onTouchMove = (e: TouchEvent) => {
      const section = sectionRef.current;
      if (!section || !dragRef.current) return;
      e.preventDefault();
      const touch = e.touches[0];
      const {
        id,
        startMouseX,
        startMouseY,
        startCardX,
        startCardY
      } = dragRef.current;
      const newX = startCardX + (touch.clientX - startMouseX);
      const newY = startCardY + (touch.clientY - startMouseY);
      setStickerPositions(prev => {
        const next = [...prev];
        next[id] = {
          x: newX,
          y: newY
        };
        return next;
      });
    };
    const onUp = () => {
      dragRef.current = null;
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    window.addEventListener('touchmove', onTouchMove, {
      passive: false
    });
    window.addEventListener('touchend', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      window.removeEventListener('touchmove', onTouchMove);
      window.removeEventListener('touchend', onUp);
    };
  }, []);
  return <>
      {/* Custom cursor — Figma user style */}
      {cursor.visible && <div style={{
      position: 'fixed',
      left: cursor.x,
      top: cursor.y,
      pointerEvents: 'none',
      zIndex: 9999,
      transform: 'translate(0, 0)'
    }} data-id="element-6" data-mp="1volw6pmrxqbj">
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none" style={{
        display: 'block'
      }} data-id="element-7" data-mp="24m5zs583zypb">
            <path d="M3 2L17 9.5L10.5 11.5L8 18L3 2Z" fill="#F5C842" stroke="white" strokeWidth="1.5" strokeLinejoin="round" data-id="element-8" data-mp="1bxijyp64q1j5" />
          </svg>
          <div style={{
        position: 'absolute',
        top: 16,
        left: 14,
        background: '#F5C842',
        color: '#1a1a1a',
        fontSize: 11,
        fontWeight: 600,
        fontFamily: 'monospace',
        letterSpacing: '0.02em',
        padding: '2px 7px',
        borderRadius: 4,
        whiteSpace: 'nowrap',
        boxShadow: '0 1px 4px rgba(0,0,0,0.15)'
      }} data-id="element-9" data-mp="14gbz1bj4nnwp">
            you
          </div>
        </div>}

      <section id="about" ref={sectionRef} className="w-full py-24 md:py-24" style={{
      background: 'transparent',
      backgroundImage: 'radial-gradient(circle, #C4C4C4 1px, transparent 1px)',
      backgroundSize: '28px 28px',
      cursor: 'none',
      position: 'relative',
      overflow: 'hidden',
      borderBottom: '1px solid var(--border)'
    }} data-id="element-10" data-mp="gqrckinrm8io">
        {/* Draggable stickers — absolute within section */}
        {stickerPositions.length > 0 && STICKERS.map((sticker, i) => <img key={sticker.id} src={sticker.src} alt="" draggable={false} onMouseDown={e => handleStickerMouseDown(e, i)} onTouchStart={e => handleStickerTouchStart(e, i)} style={{
        position: 'absolute',
        left: stickerPositions[i]?.x ?? 0,
        top: stickerPositions[i]?.y ?? 0,
        width: sticker.size,
        height: sticker.size,
        objectFit: 'contain',
        transform: `rotate(${sticker.rotation}deg)`,
        cursor: 'grab',
        userSelect: 'none',
        zIndex: stickerZ[i],
        filter: 'drop-shadow(0 3px 8px rgba(0,0,0,0.15))',
        transition: 'filter 0.15s',
        touchAction: 'none',
        WebkitUserSelect: 'none'
      }} data-id="element-11" data-mp="aweypzwv70qq" data-mpi={i} />)}

        <div className="content-wrap px-6 md:px-12" data-id="element-12" data-mp="x5lsdy06j4zp">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 md:gap-16 items-start pt-8 md:pt-4" data-id="element-13" data-mp="x76jter767tc">
            {/* Left column */}
            <div data-id="element-14" data-mp="1190ft9qg6nn6">
              <div ref={headerRef} className="reveal mb-10 md:mb-8" data-id="element-15" data-mp="1ox1iw34uxbv7">
                <p className="text-xs tracking-[0.2em] uppercase mb-3 font-[300]" style={{
                color: 'var(--muted)'
              }} data-id="element-16" data-mp="1oyurvp2y086d">
                  GET TO KNOW US
                </p>
                <h2 className="font-display text-5xl md:text-6xl tracking-[-0.03em] font-[250]" style={{
                color: 'var(--fg)'
              }} data-id="element-17" data-mp="1k4qlp81i446c">
                  About us
                </h2>
              </div>

              <div ref={leftRef} className="reveal" data-id="element-18" data-mp="2mhb97hlfoxou">
                <p className="text-[17px] leading-relaxed mb-6 text-[#4e4e4e] font-normal" data-id="element-19" data-mp="2mg05fhrznpxq">
                  GoLaunch is a product design and engineering studio focused on
                  building scalable digital systems from the ground up. We
                  partner with early-stage/pre-seed startups, scale-ups, or SMEs
                  to define their brand and product foundations and translate
                  them into rapid prototypes and production-ready platforms.
                </p>
                <p className="text-[17px] leading-relaxed text-[#4e4e4e] font-normal" data-id="element-20" data-mp="1ur6g8ujlz2c0">
                  Our lean and mean team has helped companies across the US, UK,
                  Singapore, Spain, and the Philippines raise over $4M+ USD
                  combined.
                </p>
              </div>
            </div>

            {/* Right column: metrics grid */}
            <div ref={rightRef} className="reveal md:pt-[120px]" data-id="element-21" data-mp="1fuo1izcau085">
              <div className="grid grid-cols-2 rounded-2xl overflow-hidden" style={{
              background: 'transparent',
              border: '1px solid var(--border)'
            }} data-id="element-22" data-mp="15oxqbojcpp1e">
                {[{
                value: '$4M+',
                label: 'Capital Raised'
              }, {
                value: '8–20 Weeks',
                label: 'Typical MVP Delivery'
              }, {
                value: '1-3 Weeks',
                label: 'Demo-Ready Prototype'
              }, {
                value: '5 Markets',
                label: '🇺🇸 🇵🇭 🇬🇧 🇸🇬 🇪🇸 (And counting!)'
              }].map(({
                value,
                label
              }, index) => <div key={label} className="flex flex-col justify-between p-7 md:p-8" style={{
                background: 'transparent',
                borderRight: index % 2 === 0 ? '1px solid var(--border)' : 'none',
                borderBottom: index < 2 ? '1px solid var(--border)' : 'none'
              }} data-id="element-23" data-mp="97tbcr9y2w0u" data-mpi={index}>
                    <p className="font-medium text-3xl tracking-[-0.03em] mb-2 leading-tight" style={{
                  color: 'var(--fg)'
                }} data-id="element-24" data-mp="2fu8rol8q9nj4">
                      {value}
                    </p>
                    <p className="text-sm md:text-xs leading-relaxed font-[300]" style={{
                  color: 'var(--accent)'
                }} data-id="element-25" data-mp="33l4ra4jfdn0x">
                      {label}
                    </p>
                  </div>)}
              </div>
            </div>
          </div>
        </div>
      </section>
    </>;
}